import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Users,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, parseISO } from 'date-fns';
import { getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const CalendarPage = () => {
  const { api, user, isManager, isStaff, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [staffFilter, setStaffFilter] = useState('all');
  const [staffList, setStaff] = useState([]);
  
  // For staff users, auto-filter to their own jobs
  const isStaffOnly = isStaff && !isAdmin;

  useEffect(() => {
    fetchBookings();
    if (isManager) {
      fetchStaff();
    }
  }, [currentDate, staffFilter]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const startDate = startOfMonth(subMonths(currentDate, 1));
      const endDate = endOfMonth(addMonths(currentDate, 1));
      
      const params = new URLSearchParams({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      });
      
      // Staff only sees their own jobs
      if (isStaffOnly) {
        params.append('staff_id', user.id);
      } else if (staffFilter !== 'all') {
        params.append('staff_id', staffFilter);
      }
      
      const response = await api().get(`/calendar?${params.toString()}`);
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await api().get('/users/staff/available');
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const calendarDays = useMemo(() => {
    const days = [];
    let day = calendarStart;
    while (day <= calendarEnd) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [calendarStart, calendarEnd]);

  const getBookingsForDay = (date) => {
    return bookings.filter(booking => {
      const bookingDate = parseISO(booking.scheduled_date);
      return isSameDay(bookingDate, date);
    });
  };

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  // Staff color mapping
  const getStaffColor = (staffId) => {
    const colors = [
      'bg-blue-100 border-blue-300 text-blue-800',
      'bg-green-100 border-green-300 text-green-800',
      'bg-purple-100 border-purple-300 text-purple-800',
      'bg-orange-100 border-orange-300 text-orange-800',
      'bg-pink-100 border-pink-300 text-pink-800',
      'bg-cyan-100 border-cyan-300 text-cyan-800',
    ];
    if (!staffId) return 'bg-zinc-100 border-zinc-300 text-zinc-800';
    const index = staffList.findIndex(s => s.id === staffId);
    return colors[index % colors.length];
  };

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight">
            {isStaffOnly ? 'My Calendar' : 'Calendar'}
          </h1>
          <p className="text-zinc-500 mt-1">
            {isStaffOnly ? 'Your assigned jobs' : 'View and manage your schedule'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isManager && !isStaffOnly && (
            <Select value={staffFilter} onValueChange={setStaffFilter}>
              <SelectTrigger className="w-[180px]" data-testid="staff-filter">
                <SelectValue placeholder="All Staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {staffList.map((staff) => (
                  <SelectItem key={staff.id} value={staff.id}>
                    {staff.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card className="border-zinc-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goToPreviousMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={goToNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
            </div>
            <h2 className="text-xl font-semibold text-zinc-900">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <div className="w-[100px]" /> {/* Spacer for alignment */}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-[500px]">
              <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                  <div key={day} className="text-center text-sm font-medium text-zinc-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => {
                  const dayBookings = getBookingsForDay(day);
                  const isToday = isSameDay(day, new Date());
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  
                  return (
                    <div
                      key={index}
                      className={cn(
                        'min-h-[100px] md:min-h-[120px] p-1 md:p-2 border border-zinc-100 rounded-lg',
                        !isCurrentMonth && 'bg-zinc-50/50',
                        isToday && 'border-zinc-900 bg-zinc-50'
                      )}
                    >
                      <div className={cn(
                        'text-sm font-medium mb-1',
                        isToday ? 'text-zinc-900' : isCurrentMonth ? 'text-zinc-700' : 'text-zinc-400'
                      )}>
                        {format(day, 'd')}
                      </div>
                      <div className="space-y-1">
                        {dayBookings.slice(0, 3).map((booking) => (
                          <div
                            key={booking.id}
                            onClick={() => {
                              // Staff users go to Job Hub, managers to booking detail
                              if (isStaffOnly) {
                                navigate(`/my-jobs/${booking.id}`);
                              } else {
                                navigate(`/bookings/${booking.id}`);
                              }
                            }}
                            className={cn(
                              'text-xs p-1 rounded border cursor-pointer truncate flex items-center gap-1',
                              isManager && !isStaffOnly ? getStaffColor(booking.assigned_staff_id) : getStatusColor(booking.status)
                            )}
                            title={`${booking.client_name} - ${booking.time_window_start || 'TBC'}${booking.staff_count > 1 ? ' (2 Staff)' : ''}`}
                            data-testid={`calendar-booking-${booking.id}`}
                          >
                            {/* Dual Staff Icon */}
                            {booking.staff_count > 1 && (
                              <Users className="h-3 w-3 flex-shrink-0 text-indigo-600" />
                            )}
                            <span className="hidden md:inline">{booking.time_window_start || 'TBC'} </span>
                            <span className="truncate">{booking.client_name}</span>
                          </div>
                        ))}
                        {dayBookings.length > 3 && (
                          <div className="text-xs text-zinc-500 pl-1">
                            +{dayBookings.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="border-zinc-100">
        <CardContent className="p-4">
          <h3 className="text-sm font-medium text-zinc-700 mb-2">Legend</h3>
          <div className="flex flex-wrap gap-2">
            {/* Dual Staff Indicator - shown to everyone */}
            <div className="flex items-center gap-2 px-2 py-1 rounded border bg-indigo-50 border-indigo-200 text-sm">
              <Users className="h-3 w-3 text-indigo-600" />
              2 Staff Required
            </div>
            
            {/* Staff Colors - only for managers */}
            {isManager && staffList.length > 0 && (
              <>
                {staffList.map((staff, index) => {
                  const colors = [
                    'bg-blue-100 border-blue-300',
                    'bg-green-100 border-green-300',
                    'bg-purple-100 border-purple-300',
                    'bg-orange-100 border-orange-300',
                    'bg-pink-100 border-pink-300',
                    'bg-cyan-100 border-cyan-300',
                  ];
                  return (
                    <div
                      key={staff.id}
                      className={cn(
                        'flex items-center gap-2 px-2 py-1 rounded border text-sm',
                        colors[index % colors.length]
                      )}
                    >
                      {staff.full_name}
                    </div>
                  );
                })}
                <div className="flex items-center gap-2 px-2 py-1 rounded border bg-zinc-100 border-zinc-300 text-sm">
                  Unassigned
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarPage;
